import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { fileUrl, formatPrice, formatDate, localized, currencyForLang, apiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Star, Heart, ChevronRight, Leaf, MapPin, BadgeCheck, ImageIcon, X, Repeat } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { HarvestFreshness } from "@/components/HarvestFreshness";

export default function ProductDetail() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const params = useParams();
  const id = params.id;
  const slug = params.slug;
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [newRev, setNewRev] = useState({ rating: 5, comment: "", photos: [] });
  const [submitting, setSubmitting] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [purchaseMode, setPurchaseMode] = useState("once");
  const [subBusy, setSubBusy] = useState(false);
  const [revPhotoUploading, setRevPhotoUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {photo: string} | null
  const revFileRef = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const url = slug ? `/products/by-slug/${slug}` : `/products/${id}`;
        const { data } = await api.get(url);
        setProduct(data);
        const { data: rs } = await api.get(`/reviews/${data.product_id}`);
        setReviews(rs);
        if (user) {
          try {
            const { data: ids } = await api.get("/wishlist/ids");
            setInWishlist(ids.includes(data.product_id));
          } catch {/* noop */}
        }
        // Inject canonical + hreflang tags so search engines see both locale URLs.
        const origin = window.location.origin;
        const en = data.slug ? `${origin}/p/${data.slug}` : `${origin}/product/${data.product_id}`;
        const hr = data.slug_hr ? `${origin}/proizvod/${data.slug_hr}` : en;
        const setLink = (rel, href, hreflangAttr) => {
          const sel = hreflangAttr ? `link[rel="${rel}"][hreflang="${hreflangAttr}"]` : `link[rel="${rel}"]`;
          let el = document.head.querySelector(sel);
          if (!el) {
            el = document.createElement("link");
            el.setAttribute("rel", rel);
            if (hreflangAttr) el.setAttribute("hreflang", hreflangAttr);
            document.head.appendChild(el);
          }
          el.setAttribute("href", href);
        };
        setLink("canonical", lang.startsWith("hr") ? hr : en);
        setLink("alternate", en, "en");
        setLink("alternate", hr, "hr");
        setLink("alternate", en, "x-default");
        document.title = `${data.title} · OliveOil.shop`;
      } catch {
        setProduct(null);
      }
    };
    fetch();
  }, [id, slug, user, lang]);

  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 py-16 text-ink-muted" data-testid="product-loading">Loading…</div>;
  }

  const subscribeNow = async () => {
    if (!user) return toast.error("Please sign in");
    setSubBusy(true);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        product_id: product.product_id,
        cadence: purchaseMode,
        origin_url: window.location.origin,
        currency: currencyForLang(lang).toLowerCase(),
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e, t, "errors.generic"));
      setSubBusy(false);
    }
  };

  const handleAdd = async () => {
    if (!user) return toast.error("Please sign in to add to cart");
    if (purchaseMode !== "once") return subscribeNow();
    try {
      await addItem(product.product_id, qty);
      toast.success(`Added ${qty} × ${product.title}`);
    } catch {
      toast.error("Failed to add to cart");
    }
  };

  const toggleWishlist = async () => {
    if (!user) return toast.error("Please sign in");
    try {
      const { data } = await api.post("/wishlist/toggle", { product_id: product.product_id });
      setInWishlist(data.in_wishlist);
      toast.success(data.in_wishlist ? "Saved to wishlist" : "Removed from wishlist");
    } catch { toast.error("Failed"); }
  };

  const uploadReviewPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setRevPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewRev((r) => ({ ...r, photos: [...r.photos, data.path] }));
    } catch { toast.error("Upload failed"); }
    finally { setRevPhotoUploading(false); if (revFileRef.current) revFileRef.current.value = ""; }
  };

  const submitReview = async () => {
    if (!user) return toast.error("Sign in to leave a review");
    if (!newRev.comment.trim()) return toast.error("Write a comment");
    setSubmitting(true);
    try {
      await api.post("/reviews/v2", {
        product_id: product.product_id,
        rating: newRev.rating, comment: newRev.comment, photos: newRev.photos,
      });
      const { data } = await api.get(`/reviews/${product.product_id}`);
      setReviews(data);
      const { data: p } = await api.get(`/products/${product.product_id}`);
      setProduct(p);
      setNewRev({ rating: 5, comment: "", photos: [] });
      toast.success("Thanks for your review");
    } catch {
      toast.error("Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const subPrice = (product.price * 0.9).toFixed(2);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="product-detail-page">
      <nav className="text-xs text-ink-muted flex items-center gap-1 mb-8" data-testid="breadcrumb">
        <Link to="/" className="hover:text-olive">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/shop" className="hover:text-olive">Shop</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-ink">{product.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div data-testid="product-gallery">
          <div className="aspect-[4/5] bg-boneAlt rounded-sm overflow-hidden">
            {product.images?.[activeImg] && (
              <img src={fileUrl(product.images[activeImg])} alt={product.title} className="w-full h-full object-cover" />
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-4">
              {product.images.map((img, i) => (
                <button
                  key={i} onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden rounded-sm border-2 ${i === activeImg ? "border-olive" : "border-transparent"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div data-testid="product-info">
          <p className="overline flex items-center gap-2">
            <MapPin className="w-3 h-3" />{localized(product, "region", lang)}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 leading-tight" data-testid="product-detail-title">
            {localized(product, "title", lang)}
          </h1>
          <Link to={`/vendor/${product.vendor_id}`} className="text-sm text-ink-soft underline mt-3 inline-block hover:text-olive" data-testid="product-vendor-link">
            by {product.vendor_name}
          </Link>

          <div className="flex items-center gap-6 mt-6">
            <span className="text-3xl font-serif" data-testid="product-detail-price">{formatPrice(product.price, lang)}</span>
            <span className="text-sm text-ink-muted">{product.volume_ml}ml bottle</span>
            {product.review_count > 0 && (
              <span className="text-sm flex items-center gap-1">
                <Star className="w-4 h-4 fill-current text-terracotta" />
                {product.rating.toFixed(1)} <span className="text-ink-muted">({product.review_count})</span>
              </span>
            )}
          </div>

          <p className="text-ink-soft leading-relaxed mt-8" data-testid="product-description">{localized(product, "description", lang)}</p>

          <div className="flex flex-wrap gap-2 mt-6">
            {product.organic && (
              <span className="text-xs uppercase tracking-widest text-olive border border-olive/30 px-3 py-1 flex items-center gap-1">
                <Leaf className="w-3 h-3" />Organic
              </span>
            )}
            {product.certifications?.map((c) => (
              <span key={c} className="text-xs uppercase tracking-widest text-ink-soft border border-edge px-3 py-1">{c}</span>
            ))}
          </div>

          <HarvestFreshness hoursToPress={product.hours_to_press} harvestDate={product.harvest_date} />

          <div className="mt-10" data-testid="purchase-mode-selector">
            <p className="overline mb-3">{t("product.purchase")}</p>
            <RadioGroup value={purchaseMode} onValueChange={setPurchaseMode} className="space-y-2">
              <label className={`flex justify-between items-center hairline rounded-sm p-4 cursor-pointer bg-white ${purchaseMode === "once" ? "border-olive border-2" : ""}`} data-testid="mode-once">
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="once" id="once" />
                  <div>
                    <p className="font-medium text-sm">{t("product.oneTime")}</p>
                    <p className="text-xs text-ink-muted">{formatPrice(product.price, lang)} {t("product.perBottle")}</p>
                  </div>
                </div>
              </label>
              <label className={`flex justify-between items-center hairline rounded-sm p-4 cursor-pointer bg-white ${purchaseMode === "monthly" ? "border-olive border-2" : ""}`} data-testid="mode-monthly">
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="monthly" id="monthly" />
                  <div>
                    <p className="font-medium text-sm flex items-center gap-2"><Repeat className="w-3 h-3" />{t("product.subscribeMonthly")}</p>
                    <p className="text-xs text-ink-muted">{formatPrice(subPrice, lang)} {t("product.perBottle")} · {t("product.subscribeMonthlySub")}</p>
                  </div>
                </div>
                <span className="text-xs uppercase tracking-widest text-olive">{t("product.bestValue")}</span>
              </label>
              <label className={`flex justify-between items-center hairline rounded-sm p-4 cursor-pointer bg-white ${purchaseMode === "quarterly" ? "border-olive border-2" : ""}`} data-testid="mode-quarterly">
                <div className="flex items-center gap-3">
                  <RadioGroupItem value="quarterly" id="quarterly" />
                  <div>
                    <p className="font-medium text-sm flex items-center gap-2"><Repeat className="w-3 h-3" />{t("product.subscribeQuarterly")}</p>
                    <p className="text-xs text-ink-muted">{formatPrice(subPrice, lang)} {t("product.perBottle")} · {t("product.subscribeQuarterlySub")}</p>
                  </div>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div className="flex items-center gap-4 mt-8">
            {purchaseMode === "once" && (
              <div className="flex items-center hairline rounded-sm bg-white">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3" data-testid="qty-minus">−</button>
                <span className="px-4 font-medium" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-4 py-3" data-testid="qty-plus">+</button>
              </div>
            )}
            <button
              onClick={handleAdd}
              disabled={product.stock <= 0 || subBusy}
              className="flex-1 bg-olive text-bone py-4 px-8 rounded-sm hover:bg-olive-dark transition-colors text-sm tracking-wide disabled:opacity-50"
              data-testid="add-to-cart-button"
            >
              {subBusy ? t("product.redirecting") : purchaseMode === "once" ? (product.stock > 0 ? t("product.addToCart") : t("product.outOfStock")) : t("product.subscribeNow")}
            </button>
            <button onClick={toggleWishlist} className={`hairline rounded-sm p-4 transition-colors ${inWishlist ? "bg-terracotta border-terracotta text-bone" : "bg-white hover:text-terracotta"}`} data-testid="wishlist-button">
              <Heart className={`w-5 h-5 ${inWishlist ? "fill-current" : ""}`} />
            </button>
          </div>

          <p className="text-xs text-ink-muted mt-4">{t("product.stockNote", { stock: product.stock })}</p>
        </div>
      </div>

      <section className="mt-24">
        <Tabs defaultValue="reviews" className="w-full">
          <TabsList className="bg-transparent border-b border-edge rounded-none h-auto p-0 w-full justify-start">
            <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-reviews">
              {t("product.reviews")} ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="details" className="rounded-none border-b-2 border-transparent data-[state=active]:border-olive bg-transparent px-0 mr-8 pb-3 text-sm" data-testid="tab-details">
              {t("product.details")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reviews" className="pt-10">
            {user && (
              <div className="bg-boneAlt rounded-sm p-6 mb-10" data-testid="review-form">
                <h3 className="font-serif text-2xl mb-4">{t("product.shareNotes")}</h3>
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setNewRev({ ...newRev, rating: n })} data-testid={`review-star-${n}`}>
                      <Star className={`w-6 h-6 ${n <= newRev.rating ? "fill-terracotta text-terracotta" : "text-edge"}`} />
                    </button>
                  ))}
                </div>
                <Textarea
                  value={newRev.comment} onChange={(e) => setNewRev({ ...newRev, comment: e.target.value })}
                  placeholder={t("product.reviewPlaceholder")}
                  className="rounded-sm bg-white"
                  data-testid="review-comment"
                />
                {newRev.photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="review-photos-preview">
                    {newRev.photos.map((p, i) => (
                      <div key={i} className="relative w-20 h-20 rounded-sm overflow-hidden hairline">
                        <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setNewRev({ ...newRev, photos: newRev.photos.filter((_, j) => j !== i) })} className="absolute top-0 right-0 bg-white/90 p-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-4">
                  <Button onClick={submitReview} disabled={submitting} className="bg-olive hover:bg-olive-dark text-bone rounded-sm" data-testid="review-submit">
                    {submitting ? t("product.redirecting") : t("product.postReview")}
                  </Button>
                  <label className="cursor-pointer text-sm flex items-center gap-2 text-ink-soft hover:text-olive" data-testid="review-photo-label">
                    <ImageIcon className="w-4 h-4" />
                    {revPhotoUploading ? "…" : t("product.addPhoto")}
                    <input ref={revFileRef} type="file" accept="image/*" className="hidden" onChange={uploadReviewPhoto} data-testid="review-photo-input" />
                  </label>
                </div>
              </div>
            )}
            <div className="space-y-6" data-testid="reviews-list">
              {reviews.length === 0 && <p className="text-ink-muted">{t("product.noReviews")}</p>}
              {reviews.map((r) => (
                <div key={r.review_id} className="border-b border-edge pb-6" data-testid={`review-${r.review_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <p className="font-medium">{r.user_name}</p>
                      {r.verified_purchase && (
                        <span className="text-[10px] uppercase tracking-widest bg-olive/10 text-olive border border-olive/30 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`verified-${r.review_id}`}>
                          <BadgeCheck className="w-3 h-3" />{t("product.verifiedBuyer")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-terracotta text-terracotta" : "text-edge"}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-ink-soft mt-2 leading-relaxed">{r.comment}</p>
                  {r.photos?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid={`review-photos-${r.review_id}`}>
                      {r.photos.map((p, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightbox({ photo: p })}
                          className="w-24 h-24 rounded-sm overflow-hidden hairline block focus:outline-none focus:ring-2 focus:ring-olive transition-transform hover:scale-[1.03]"
                          data-testid={`review-photo-${r.review_id}-${i}`}
                          aria-label={t("product.viewPhoto")}
                        >
                          <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-ink-muted mt-2">{formatDate(r.created_at, lang)}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="details" className="pt-10">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 max-w-2xl">
              <dt className="overline">{t("product.type")}</dt><dd className="text-sm capitalize">{product.type.replace("_", " ")}</dd>
              <dt className="overline">{t("product.volume")}</dt><dd className="text-sm">{product.volume_ml}ml</dd>
              <dt className="overline">{t("product.region")}</dt><dd className="text-sm">{product.region}</dd>
              <dt className="overline">{t("product.producer")}</dt><dd className="text-sm">{product.vendor_name}</dd>
              <dt className="overline">{t("product.certifications")}</dt><dd className="text-sm">{product.certifications.join(", ") || "—"}</dd>
              <dt className="overline">{t("product.organic")}</dt><dd className="text-sm">{product.organic ? t("common.yes") : t("common.no")}</dd>
            </dl>
          </TabsContent>
        </Tabs>
      </section>

      {/* Review photo lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="max-w-3xl bg-bone p-0 border-0" data-testid="review-photo-lightbox">
          {lightbox && (
            <img
              src={fileUrl(lightbox.photo)}
              alt=""
              className="w-full h-auto max-h-[85vh] object-contain rounded-sm"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
